'use client';

import { c as _c } from "react/compiler-runtime";
import type { BigIntish, MarketId } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils, type InterfaceToken, type MarketForRewards } from '@morpho-org/dapp-core';
import { Avatar, NetworkAvatar as Network } from '@morpho-org/ui-v6/client';
import { Card, Skeleton } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import { TruncatedTooltip } from '@repo/ui';
import { memo, useMemo } from 'react';
import { AutoDeleverageButton } from '@/components/common/AutoDeleverage/AutoDeleverageButton';
import { useIsAutoDeleverageEnabled } from '@/components/common/AutoDeleverage/hooks/useIsAutoDeleverageEnabled';
import { ProtectedLltv, ProtectedLltvLabel } from '@/components/common/AutoDeleverage/ProtectedLltv';
import { IconRewardHover } from '@/components/common/IconRewardHover';
import ValueTransition from '@/components/common/ValueTransition';
import { type MarketSummaryPosition, useMarketSummaryData } from './hooks/useMarketSummaryData';
interface MarketSummaryCardProps {
  marketId: MarketId;
  /** Current on-chain position. */
  current: MarketSummaryPosition;
  /** Position after the pending action (undefined fields ⇒ no "after" value shown). */
  after: MarketSummaryPosition;
  collateralAsset: InterfaceToken;
  loanAsset: InterfaceToken;
  lltv?: BigIntish;
  isLoading: boolean;
  avgBorrowApy?: number | null;
  marketForRewards?: MarketForRewards | null;
  /**
   * Card surface. `card` (default) is the elevated `bg-card` look used in the market-page
   * simple panel (matching the vault page's `VaultSummary`). `muted` is the inset
   * `bg-muted shadow-none` look used inside the Position Manager dialogs, matching the vault
   * Position Manager card (`TransactionInfoCard.Root`).
   */
  background?: 'card' | 'muted';
}
const MarketSummaryCard = t0 => {
  const $ = _c(143);
  const {
    marketId,
    collateralAsset,
    loanAsset,
    current,
    after,
    lltv,
    isLoading,
    avgBorrowApy,
    marketForRewards,
    background: t1
  } = t0;
  const background = t1 === undefined ? "card" : t1;
  const chainId = loanAsset.chain.id;
  const isAutoDeleverageEnabled = useIsAutoDeleverageEnabled(chainId);
  let t2;
  if ($[0] !== after || $[1] !== avgBorrowApy || $[2] !== chainId || $[3] !== collateralAsset || $[4] !== current || $[5] !== lltv || $[6] !== loanAsset || $[7] !== marketForRewards || $[8] !== marketId) {
    t2 = {
      marketId,
      chainId,
      initialData: current,
      finalData: after,
      collateralAsset,
      loanAsset,
      lltv,
      avgBorrowApy,
      marketForRewards
    };
    $[0] = after;
    $[1] = avgBorrowApy;
    $[2] = chainId;
    $[3] = collateralAsset;
    $[4] = current;
    $[5] = lltv;
    $[6] = loanAsset;
    $[7] = marketForRewards;
    $[8] = marketId;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const {
    collateral,
    loan,
    ltv,
    apy: apyData
  } = useMarketSummaryData(t2);
  let t3;
  if ($[10] !== chainId) {
    t3 = InterfaceChainUtils.getMetadata(chainId);
    $[10] = chainId;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const networkName = t3.chain.name;
  const t4 = background === "muted" && "bg-muted shadow-none";
  let t5;
  if ($[12] !== t4) {
    t5 = cn("gap-sm flex-col justify-between", t4);
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = background === "card";
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="text-3xs text-secondary-foreground">Network</span>;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      size: "small"
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== chainId) {
    t9 = <Network chainId={chainId} srcAvatarProps={t8} />;
    $[16] = chainId;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== networkName) {
    t10 = <span className="text-3xs">{networkName}</span>;
    $[18] = networkName;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <div className="flex min-h-[20px] flex-row items-center justify-between">{t7}<div className="gap-2xs flex flex-row items-center">{t9}{t10}</div></div>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== loanAsset.logoURI) {
    t12 = <Avatar size="small" src={loanAsset.logoURI} />;
    $[23] = loanAsset.logoURI;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = `Loan (${loanAsset.symbol})`;
  let t14;
  if ($[25] !== loanAsset.symbol) {
    t14 = labelRef => <span ref={labelRef} className="text-3xs text-secondary-foreground min-w-0 truncate">{`Loan (${loanAsset.symbol})`}</span>;
    $[25] = loanAsset.symbol;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t13 || $[28] !== t14) {
    t15 = <TruncatedTooltip text={t13}>{t14}</TruncatedTooltip>;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t12 || $[31] !== t15) {
    t16 = <div className="gap-2xs flex min-w-0 items-center">{t12}{t15}</div>;
    $[30] = t12;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = loan.showFinalValue ? "text-muted-foreground" : "text-foreground";
  let t18;
  if ($[33] !== loan.initial || $[34] !== loan.initialDetailed || $[35] !== t17) {
    t18 = {
      value: loan.initial,
      tooltip: loan.initialDetailed,
      colorClass: t17
    };
    $[33] = loan.initial;
    $[34] = loan.initialDetailed;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== loan.final || $[38] !== loan.finalDetailed) {
    t19 = {
      value: loan.final,
      tooltip: loan.finalDetailed,
      colorClass: "text-foreground"
    };
    $[37] = loan.final;
    $[38] = loan.finalDetailed;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== isLoading) {
    t20 = {
      isLoading,
      isFetchingFinalValue: false
    };
    $[40] = isLoading;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== loan.showFinalValue) {
    t21 = {
      showFinalValue: loan.showFinalValue,
      testId: "loan-value"
    };
    $[42] = loan.showFinalValue;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t18 || $[45] !== t19 || $[46] !== t20 || $[47] !== t21) {
    t22 = <div className="flex flex-[1px] justify-end"><ValueTransition initial={t18} final={t19} loading={t20} config={t21} /></div>;
    $[44] = t18;
    $[45] = t19;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t16 || $[50] !== t22) {
    t23 = <div className="flex min-h-[20px] flex-row">{t16}{t22}</div>;
    $[49] = t16;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== collateralAsset.logoURI) {
    t24 = <Avatar size="small" src={collateralAsset.logoURI} />;
    $[52] = collateralAsset.logoURI;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  const t25 = `Collateral (${collateralAsset.symbol})`;
  let t26;
  if ($[54] !== collateralAsset.symbol) {
    t26 = labelRef_0 => <span ref={labelRef_0} className="text-3xs text-secondary-foreground min-w-0 truncate" style={{
      lineHeight: "normal"
    }}>{`Collateral (${collateralAsset.symbol})`}</span>;
    $[54] = collateralAsset.symbol;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t25 || $[57] !== t26) {
    t27 = <TruncatedTooltip text={t25}>{t26}</TruncatedTooltip>;
    $[56] = t25;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t24 || $[60] !== t27) {
    t28 = <div className="gap-2xs flex min-w-0 items-center">{t24}{t27}</div>;
    $[59] = t24;
    $[60] = t27;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  const t29 = collateral.showFinalValue ? "text-muted-foreground" : "text-foreground";
  let t30;
  if ($[62] !== collateral.initial || $[63] !== collateral.initialDetailed || $[64] !== t29) {
    t30 = {
      value: collateral.initial,
      tooltip: collateral.initialDetailed,
      colorClass: t29
    };
    $[62] = collateral.initial;
    $[63] = collateral.initialDetailed;
    $[64] = t29;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== collateral.final || $[67] !== collateral.finalDetailed) {
    t31 = {
      value: collateral.final,
      tooltip: collateral.finalDetailed,
      colorClass: "text-foreground"
    };
    $[66] = collateral.final;
    $[67] = collateral.finalDetailed;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== isLoading) {
    t32 = {
      isLoading
    };
    $[69] = isLoading;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== collateral.showFinalValue) {
    t33 = {
      showFinalValue: collateral.showFinalValue,
      testId: "collateral-value"
    };
    $[71] = collateral.showFinalValue;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== t30 || $[74] !== t31 || $[75] !== t32 || $[76] !== t33) {
    t34 = <div className="flex flex-[1px] justify-end"><ValueTransition initial={t30} final={t31} loading={t32} config={t33} /></div>;
    $[73] = t30;
    $[74] = t31;
    $[75] = t32;
    $[76] = t33;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  let t35;
  if ($[78] !== t28 || $[79] !== t34) {
    t35 = <div className="flex min-h-[20px] flex-row">{t28}{t34}</div>;
    $[78] = t28;
    $[79] = t34;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  let t36;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <span className="text-3xs text-secondary-foreground truncate" style={{
      maxWidth: 240
    }}>LTV</span>;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  const t37 = ltv.isInitialUnhealthy ? "text-destructive-foreground" : ltv.showFinalValue ? "text-muted-foreground" : "text-foreground";
  let t38;
  if ($[82] !== ltv.initial || $[83] !== t37) {
    t38 = {
      value: ltv.initial,
      colorClass: t37
    };
    $[82] = ltv.initial;
    $[83] = t37;
    $[84] = t38;
  } else {
    t38 = $[84];
  }
  let t39;
  if ($[85] !== ltv.final || $[86] !== ltv.isFinalUnhealthy) {
    t39 = ltv.final ? {
      value: ltv.final,
      colorClass: ltv.isFinalUnhealthy ? "text-destructive-foreground" : "text-foreground"
    } : undefined;
    $[85] = ltv.final;
    $[86] = ltv.isFinalUnhealthy;
    $[87] = t39;
  } else {
    t39 = $[87];
  }
  let t40;
  if ($[88] !== isLoading) {
    t40 = {
      isLoading
    };
    $[88] = isLoading;
    $[89] = t40;
  } else {
    t40 = $[89];
  }
  let t41;
  if ($[90] !== ltv.isFinalUnhealthy || $[91] !== ltv.showFinalValue) {
    t41 = {
      showFinalValue: ltv.showFinalValue,
      testId: "ltv-value",
      showErrorIcon: ltv.isFinalUnhealthy
    };
    $[90] = ltv.isFinalUnhealthy;
    $[91] = ltv.showFinalValue;
    $[92] = t41;
  } else {
    t41 = $[92];
  }
  let t42;
  if ($[93] !== t38 || $[94] !== t39 || $[95] !== t40 || $[96] !== t41) {
    t42 = <div className="flex min-h-[20px] flex-row items-center justify-between">{t36}<ValueTransition initial={t38} final={t39} loading={t40} config={t41} /></div>;
    $[93] = t38;
    $[94] = t39;
    $[95] = t40;
    $[96] = t41;
    $[97] = t42;
  } else {
    t42 = $[97];
  }
  let t43;
  if ($[98] !== chainId || $[99] !== marketId) {
    t43 = <span className="text-3xs text-secondary-foreground"><ProtectedLltvLabel defaultLabel="Liquidation LTV" label="Auto-Deleverage LLTV" marketId={marketId} chainId={chainId} /></span>;
    $[98] = chainId;
    $[99] = marketId;
    $[100] = t43;
  } else {
    t43 = $[100];
  }
  let t44;
  if ($[101] !== chainId || $[102] !== isAutoDeleverageEnabled || $[103] !== marketId) {
    t44 = isAutoDeleverageEnabled && <AutoDeleverageButton marketId={marketId} chainId={chainId} />;
    $[101] = chainId;
    $[102] = isAutoDeleverageEnabled;
    $[103] = marketId;
    $[104] = t44;
  } else {
    t44 = $[104];
  }
  let t45;
  if ($[105] !== t43 || $[106] !== t44) {
    t45 = <div className="gap-2xs flex items-center">{t43}{t44}</div>;
    $[105] = t43;
    $[106] = t44;
    $[107] = t45;
  } else {
    t45 = $[107];
  }
  let t46;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = {
      lineHeight: "normal"
    };
    $[108] = t46;
  } else {
    t46 = $[108];
  }
  let t47;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <Skeleton className="h-[14px] w-[30px] rounded-[20px]" />;
    $[109] = t47;
  } else {
    t47 = $[109];
  }
  let t48;
  if ($[110] !== chainId || $[111] !== lltv || $[112] !== marketId) {
    t48 = <span className="text-3xs text-foreground" style={t46} data-testid="final-ltv-value"><ProtectedLltv lltv={lltv} marketId={marketId} chainId={chainId} skeleton={t47} /></span>;
    $[110] = chainId;
    $[111] = lltv;
    $[112] = marketId;
    $[113] = t48;
  } else {
    t48 = $[113];
  }
  let t49;
  if ($[114] !== t45 || $[115] !== t48) {
    t49 = <div className="flex h-[26px] flex-row items-center justify-between">{t45}{t48}</div>;
    $[114] = t45;
    $[115] = t48;
    $[116] = t49;
  } else {
    t49 = $[116];
  }
  let t50;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <span className="text-3xs text-secondary-foreground" style={{
      lineHeight: "normal"
    }}>Rate</span>;
    $[117] = t50;
  } else {
    t50 = $[117];
  }
  let t51;
  if ($[118] !== apyData.apy || $[119] !== apyData.netApy || $[120] !== apyData.points || $[121] !== apyData.rewards || $[122] !== collateralAsset) {
    t51 = {
      apy: apyData.apy,
      rewards: apyData.rewards,
      points: apyData.points,
      variant: "market",
      netApy: apyData.netApy,
      asset: collateralAsset
    };
    $[118] = apyData.apy;
    $[119] = apyData.netApy;
    $[120] = apyData.points;
    $[121] = apyData.rewards;
    $[122] = collateralAsset;
    $[123] = t51;
  } else {
    t51 = $[123];
  }
  let t52;
  if ($[124] !== chainId || $[125] !== loanAsset.address || $[126] !== marketId) {
    t52 = {
      kind: "market",
      chainId,
      marketId,
      loanTokenAddress: loanAsset.address
    };
    $[124] = chainId;
    $[125] = loanAsset.address;
    $[126] = marketId;
    $[127] = t52;
  } else {
    t52 = $[127];
  }
  let t53;
  if ($[128] !== apyData.formattedNetApy) {
    t53 = <span className="text-3xs" data-testid="market-summary-rate">{apyData.formattedNetApy}</span>;
    $[128] = apyData.formattedNetApy;
    $[129] = t53;
  } else {
    t53 = $[129];
  }
  let t54;
  if ($[130] !== t51 || $[131] !== t52 || $[132] !== t53) {
    t54 = <div className="flex min-h-[20px] flex-row items-center justify-between">{t50}<IconRewardHover RewardsHoverCardProps={t51} merklContext={t52} isReversed={true} iconSize={20}>{t53}</IconRewardHover></div>;
    $[130] = t51;
    $[131] = t52;
    $[132] = t53;
    $[133] = t54;
  } else {
    t54 = $[133];
  }
  let t55;
  if ($[134] !== t11 || $[135] !== t23 || $[136] !== t35 || $[137] !== t42 || $[138] !== t49 || $[139] !== t5 || $[140] !== t54 || $[141] !== t6) {
    t55 = <Card className={t5} withBorder={t6}>{t11}{t23}{t35}{t42}{t49}{t54}</Card>;
    $[134] = t11;
    $[135] = t23;
    $[136] = t35;
    $[137] = t42;
    $[138] = t49;
    $[139] = t5;
    $[140] = t54;
    $[141] = t6;
    $[142] = t55;
  } else {
    t55 = $[142];
  }
  return t55;
};
export default memo(MarketSummaryCard);